import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react"

import { FirebaseContext } from "../../../../firebase"
import { UserContext } from "../../../../context/userContext"
import { gql, useLazyQuery } from "@apollo/client"

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000

// Utility function to check if cache is valid
const isCacheValid = (timestamp) => {
  return timestamp && Date.now() - timestamp < CACHE_TTL
}

// Get cached data with validation
const getValidCache = (cacheKey) => {
  try {
    const cachedData = localStorage.getItem(cacheKey)
    if (!cachedData) return null

    const { data, timestamp } = JSON.parse(cachedData)
    if (!isCacheValid(timestamp)) {
      localStorage.removeItem(cacheKey)
      return null
    }

    return data
  } catch (e) {
    console.error("Error reading from cache:", e)
    return null
  }
}

// Set data in cache
const setCache = (cacheKey, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    }
    localStorage.setItem(cacheKey, JSON.stringify(cacheData))
  } catch (e) {
    console.error("Error setting cache:", e)
  }
}

export default function useMenuItems(menuType) {
  // Use a ref to track if data has already been loaded
  const dataLoaded = useRef(false)
  const metricsRef = useRef({
    fbMicroservicesStart: 0,
    fbMicroservicesEnd: 0,
    fbTemplatesStart: 0,
    fbTemplatesEnd: 0,
    graphqlStart: 0,
    graphqlEnd: 0,
    menuFetchStart: 0,
    menuFetchEnd: 0,
  })

  const [loading, setLoading] = useState(true)
  const [fetching, setFetching] = useState(false)
  const [items, setItems] = useState([])

  const [microservices, setMicroservices] = useState([])
  const [templates, setTemplates] = useState({})
  const [membershipsList, setMembershipsList] = useState([])
  const [cachedItemsLoaded, setCachedItemsLoaded] = useState(false)

  const firebase = useContext(FirebaseContext)
  const { session } = useContext(UserContext)
  const userCtx = useContext(UserContext)

  const enterpriseId = session.user.enterprise_id
  const organizationId = session.user.organization_id
  const staffId = session.user.staff_id

  // Cache keys
  const microservicesCacheKey = `microservices-${enterpriseId}-${organizationId}`
  const templatesCacheKey = `templates-${enterpriseId}`
  const membershipsCacheKey = `memberships-${staffId}`
  const menuCacheKey = `menu-${menuType}-${staffId}-${organizationId}`

  const entCollection = `enterprise-config/${enterpriseId}`

  const STAFF_MEMBERSHIP_ORGANIZATION = gql`
    query getStaffMembershipDetailByStaffID($staff_id: ID!) {
      userMembership: getStaffMembershipDetailByStaffID(staff_id: $staff_id) {
        membership_list
      }
    }
  `

  const [getStaffMembershipDetail] = useLazyQuery(STAFF_MEMBERSHIP_ORGANIZATION)

  // Initial load from cache
  useEffect(() => {
    // Try to load final menu items from cache first (fastest path)
    const cachedItems = getValidCache(menuCacheKey)
    if (cachedItems) {
      setItems(cachedItems)
      setLoading(false)
      setCachedItemsLoaded(true)
      return
    }

    // Load data from individual caches if available
    const cachedMicroservices = getValidCache(microservicesCacheKey)
    if (cachedMicroservices) {
      setMicroservices(cachedMicroservices)
    }

    const cachedTemplates = getValidCache(templatesCacheKey)
    if (cachedTemplates) {
      setTemplates(cachedTemplates)
    }

    const cachedMemberships = getValidCache(membershipsCacheKey)
    if (cachedMemberships) {
      setMembershipsList(cachedMemberships)
    }

    // Continue with normal loading if we don't have the final menu cached
  }, [
    menuCacheKey,
    microservicesCacheKey,
    templatesCacheKey,
    membershipsCacheKey,
  ])

  // Memoize handleDependencies function to prevent unnecessary recreations
  const handleDependencies = useCallback(
    (depObj, depItems) => {
      let result = { hidden: false, newItem: {} }

      for (const [depType, depValue] of Object.entries(depObj)) {
        switch (depType) {
          case "template_enabled": {
            const templateId = depValue
            if (templateId in templates) {
              if (templates[depValue]?.template_availability === 0) {
                result = { hidden: true }
                break
              }
            }
            result = { hidden: false }
            break
          }
          case "template_id": {
            const templateId = depValue
            if (templateId in templates) {
              if (templates[depValue]?.template_availability === 0) {
                result = { hidden: true }
                break
              }
              result = {
                hidden: false,
                newItem: {
                  label: templates[depValue]?.template_title,
                },
              }
              break
            }
            result = { hidden: false }
            break
          }
          case "template_type": {
            const newItems = depItems.reduce((acc, item) => {
              const isCustom = item.id.startsWith("custom")
              if (isCustom && !(item?.id in templates)) {
                return acc
              }
              if (item?.id in templates) {
                const notAvailable =
                  templates[item.id]?.template_availability === 0
                if (notAvailable) return acc
                return [
                  ...acc,
                  {
                    ...item,
                    label: templates[item.id]?.template_title,
                  },
                ]
              }
              return [...acc, item]
            }, [])
            if (newItems.length === 0) {
              result = { hidden: true }
              break
            }
            if (depValue === "session_module") {
              if (
                newItems.length === 1 &&
                newItems[0].id === "all_engagements"
              ) {
                result = { hidden: true }
                break
              }
            }
            result = {
              hidden: false,
              newItem: { items: newItems },
            }
            break
          }
          case "microservice_id": {
            if (!microservices.includes(depValue)) {
              result = { hidden: true }
              break
            }
            break
          }
          case "participant_membership_status": {
            if (depValue.includes(userCtx.participant_membership_status)) {
              result = { hidden: true }
              break
            }
            break
          }
          case "profile_type": {
            if (depValue === "self") {
              let shouldDisplay = { hidden: false }
              const { current_staff_membership_id } = userCtx
              const showAccountMenu = membershipsList.includes(
                current_staff_membership_id
              )
              if (!showAccountMenu) {
                shouldDisplay = { hidden: true }
              }
              result = shouldDisplay
              break
            }
            break
          }
          default: {
            result = { hidden: true }
            break
          }
        }
      }

      return result
    },
    [templates, microservices, membershipsList, userCtx]
  )

  // Initialize microservices and templates - only executed once
  useEffect(() => {
    // Skip data fetching if we already have cached menu items
    if (cachedItemsLoaded) return

    // Skip if data already loaded in this session
    if (dataLoaded.current) return

    setLoading(true)

    // Load microservices
    const loadMicroservices = async () => {
      if (microservices.length > 0) return // Already loaded

      const orgMicroservices = firebase.db.collection(
        `${entCollection}/organizations/${organizationId}/microservices/`
      )

      metricsRef.current.fbMicroservicesStart = performance.now()

      try {
        // Use get() instead of onSnapshot for initial load
        const snapshot = await orgMicroservices.get()

        metricsRef.current.fbMicroservicesEnd = performance.now()

        let enabledMicroservices = []
        snapshot.docs.forEach((doc) => {
          const microserviceId = Number(doc.id)
          const isEnabled = doc.data().enable
          if (isEnabled) {
            enabledMicroservices.push(microserviceId)
          }
        })

        setMicroservices(enabledMicroservices)
        setCache(microservicesCacheKey, enabledMicroservices)
      } catch (error) {
        console.error("Error loading microservices:", error)
      }
    }

    // Load templates
    const loadTemplates = async () => {
      if (Object.keys(templates).length > 0) return // Already loaded

      // Fetch enterprise custom templates
      const templateDocsCustom = firebase.db.collection(`${entCollection}/custom-templates/`)
      // Fetch system templates
      const templateDocsSystem = firebase.db.collection(`system-templates/`)

      metricsRef.current.fbTemplatesStart = performance.now()

      try {
        // Use get() instead of onSnapshot for initial load
        const [snapshotCustom, snapshotSystem] = await Promise.all([
          templateDocsCustom.get(),
          templateDocsSystem.get(),
        ])

        metricsRef.current.fbTemplatesEnd = performance.now()

        let templateList = {}
        // Combine custom and system templates into one list
        snapshotCustom.docs.forEach((doc) => {
          templateList[doc.id] = doc.data()
        })
        snapshotSystem.docs.forEach((doc) => {
          // Skip if custom template overrides system template
          if (doc.id in templateList) return
          templateList[doc.id] = doc.data()
        })

        setTemplates(templateList)
        setCache(templatesCacheKey, templateList)
      } catch (error) {
        console.error("Error loading templates:", error)
      }
    }

    // Load memberships
    const loadMemberships = async () => {
      if (membershipsList.length > 0 || fetching) return // Already loaded or loading

      if (menuType === "staff") {
        //setFetching(true)
        metricsRef.current.graphqlStart = performance.now()

        getStaffMembershipDetail({
          variables: {
            staff_id: staffId,
            organization_id: organizationId,
          },
          onCompleted: (data) => {
            metricsRef.current.graphqlEnd = performance.now()

            const membershipList = data?.userMembership?.membership_list || []
            setMembershipsList(membershipList)
            setCache(membershipsCacheKey, membershipList)
            setFetching(false)
          },
          onError: (error) => {
            metricsRef.current.graphqlEnd = performance.now()
            console.error("Error fetching membership details", error)
            setFetching(false)
          },
        })
      }
    }

    // Execute all data loading functions in parallel
    Promise.all([loadMicroservices(), loadTemplates(), loadMemberships()]).then(
      () => {
        dataLoaded.current = true
        setLoading(false)
      }
    )

    // No cleanup needed as we're using get() instead of onSnapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cachedItemsLoaded,
    dataLoaded,
    enterpriseId,
    firebase.db,
    fetching,
    membershipsList.length,
    menuType,
    microservices.length,
    organizationId,
    staffId,
    templates,
    microservicesCacheKey,
    templatesCacheKey,
    membershipsCacheKey,
    getStaffMembershipDetail,
  ])

  // Memoize microservices and templates to avoid unnecessary recreations
  const memoizedMicroservices = useMemo(() => microservices, [microservices])
  const memoizedTemplates = useMemo(() => templates, [templates])
  const memoizedMembershipsList = useMemo(
    () => membershipsList,
    [membershipsList]
  )

  // Load menu after microservices and templates have been loaded
  useEffect(() => {
    // Skip if we already loaded cached menu items
    if (cachedItemsLoaded) return

    // Skip if still loading or already have items
    if (loading || fetching) return
    if (items.length > 0) return // Avoid reloads if we already have items

    let menuDoc

    if (menuType === "staff") {
      menuDoc = firebase.db.doc("system-ui/menu-staff")
    } else if (menuType === "participant") {
      menuDoc = firebase.db.doc("system-ui/menu-participant")
    } else {
      return // Do nothing if menu type is not valid
    }

    const fetchMenuItems = async () => {
      try {
        metricsRef.current.menuFetchStart = performance.now()

        const doc = await menuDoc.get()
        if (doc.exists) {
          const menuDef = doc.data()

          let itemsList = []
          for (const [key, value] of Object.entries(menuDef)) {
            if (value.required) {
              itemsList.push({
                id: key,
                ...value,
              })
            }
            if ("dependencies" in value) {
              const { dependencies, items } = value
              const { hidden, newItem = {} } = handleDependencies(
                dependencies,
                items
              )
              if (hidden) continue

              itemsList.push({
                id: key,
                ...value,
                ...newItem,
              })
            }
          }
          itemsList.sort((a, b) => a.index - b.index)

          metricsRef.current.menuFetchEnd = performance.now()

          // Cache the final processed menu items
          setCache(menuCacheKey, itemsList)

          setItems(itemsList)
        } else {
          console.log("Could not find document to load default Menu.")
        }
      } catch (error) {
        console.error("Error fetching menu: ", error)
      }
    }

    fetchMenuItems()
  }, [
    cachedItemsLoaded,
    loading,
    fetching,
    memoizedMicroservices,
    memoizedTemplates,
    memoizedMembershipsList,
    handleDependencies,
    menuType,
    firebase.db,
    items.length,
    menuCacheKey,
  ])

  const isLoading = loading || fetching

  return [isLoading, items]
}
